import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
// react-quill CSS removed - replaced by TipTap

dayjs.extend(utc)
dayjs.extend(timezone)
import {
  SectionCard,
  SectionHeader,
  SectionContent,
  SubSectionCard,
  DetailRow,
  DetailLabel,
} from './printableReport/SectionCard'
import { withAsyncPaginate } from 'react-select-async-paginate'
import Creatable from 'react-select/creatable'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getParseDataFromHtmlString } from 'src/consumer/helpers/utilFunctions'
import styled from 'styled-components'
import {
  PMPHeaderDetails,
  PMPHeadingBanner,
  PMPSectionTextAreaBox,
} from '../PrescriptionMakingPage/PrescriptionMakingPageStyles'
import PdfReportSectionHandler from './pdfReport/PdfReportSectionHandler'
import { getSectionsAndQuestionsBasedOnFilledAnswers } from './util'
import React, { useEffect } from 'react'

export function getDateOfScreeningReport(screeningReportData, withTime) {
  let format = 'DD/MM/YYYY'
  if (withTime) {
    format = 'DD/MM/YYYY h:mmA'
  }
  if (screeningReportData?.createdTime) {
    return dayjs
      .utc(
        screeningReportData?.createdTime || screeningReportData?.modifiedTime,
        'YYYY-MM-DD HH:mm:ss',
      )
      .local()
      .format(format)
  }
  return dayjs().format(format)
}

export const ScreeningReportHeader = ({
  screeningReportData,
  screenerName,
  doctorName,
  timesVisit,
  clinicBrand,
  templateData,
  leaveSpace = false, // Add leaveSpace prop to support blank header space
}) => {
  return (
    <div style={{ background: '#FFF' }}>
      {leaveSpace && <div style={{ height: '120px' }}></div>}
      {!leaveSpace && (
        <>
          <PMPHeadingBanner noColor={true}>
            {templateData?.headingText || 'Screening Report'}
          </PMPHeadingBanner>
          <PMPHeaderDetails noColor={true} style={{
            padding: '16px 24px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            borderBottom: '1px solid #e2e8f0',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px 24px',
            alignItems: 'start',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
              <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Patient Name:{' '}
                <span style={{ textTransform: 'capitalize', color: '#1e293b', fontWeight: 600 }}>
                  {screeningReportData?.patientName ?? '—'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Age/Gender:{' '}
                <span style={{ color: '#1e293b', fontWeight: 600 }}>
                  {getAgeFromDobWithTime(screeningReportData?.patientDateOfBirth)}/
                  {GENDER_LIST_ONBOARDING?.find((item) => item.value === screeningReportData?.patientGender)?.label ?? '—'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Date & time:{' '}
                <span style={{ color: '#1e293b', fontWeight: 600 }}>{getDateOfScreeningReport(screeningReportData, true)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, textAlign: 'right' }}>
              <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Screening consulted for:{' '}
                <span style={{ color: '#1e293b', fontWeight: 600 }}>{screeningReportData?.requestingDoctorName || 'NA'}</span>
              </div>
              {screenerName && (
                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                  Screened by:{' '}
                  <span style={{ color: '#1e293b', fontWeight: 600 }}>{screenerName}</span>
                </div>
              )}
              <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Visit No:{' '}
                <span style={{ color: '#1e293b', fontWeight: 600 }}>{timesVisit || 1}</span>
              </div>
            </div>
          </PMPHeaderDetails>
        </>
      )}
    </div>
  )
}

export const ScreeningReportBodyFirstSection = ({
  observationAndAdvice,
  selectedComplaints,
  selectedDiagnosis,
  screeningQuestionAnswers,
  screeningSections,
  reportData,
  primaryClinicalDiagnosis,
  differentialClinicalDiagnoses,
  doctorSpecializations = null,
  visitType = null,
  useDebugNumbering = false, // Only use "Question 1", "Question 2" for debugging in history drawer
  screeningForSpecializations = null, // Specializations saved with the report (for history drawer)
}) => {
  const [sectionsWithAnswers, setSectionsWithAnswers] = React.useState([])
  const lastProcessedKeyRef = React.useRef('')
  
  React.useEffect(() => {
    const hasAnswers = screeningQuestionAnswers && screeningQuestionAnswers.length > 0
    const hasSections = screeningSections && screeningSections.length > 0
    
    if (!hasAnswers) {
      setSectionsWithAnswers([])
      lastProcessedKeyRef.current = ''
      return
    }
    
    // Create a stable key based on answer/question IDs to prevent duplicate processing
    const answersKey = hasAnswers 
      ? screeningQuestionAnswers.map(a => a?.questionId).filter(Boolean).sort().join(',')
      : ''
    const sectionsKey = hasSections 
      ? screeningSections.map(s => s?.id).filter(Boolean).sort().join(',')
      : ''
    const processKey = `${answersKey}|${sectionsKey}`
    
    // Skip if we've already processed this exact combination
    // But we need to check if we actually have results - if not, reprocess
    const hasResults = sectionsWithAnswers && sectionsWithAnswers.length > 0
    
    if (processKey === lastProcessedKeyRef.current) {
      if (hasResults) {
        return
      } else {
        // Clear the key to force reprocessing
        lastProcessedKeyRef.current = ''
      }
    }
    
    // Only fetch if sections are completely missing (not passed from parent)
    // If sections are passed (even if they don't match), use them and don't fetch
    const needsFetch = hasAnswers && !hasSections
    
    // Determine effective specializations - prioritize screeningForSpecializations from saved report, then other sources
    // Convert screeningForSpecializations to array if it's a string
    const screeningForSpecializationsArray = screeningForSpecializations
      ? (Array.isArray(screeningForSpecializations)
          ? screeningForSpecializations
          : (typeof screeningForSpecializations === 'string'
              ? screeningForSpecializations.split(',').filter(Boolean)
              : []))
      : null
    
    const effectiveSpecializations = 
      screeningForSpecializationsArray ||  // First priority: from saved report
      doctorSpecializations || 
      reportData?.doctorSpecializations || 
      reportData?.mentorSpecializations ||
      reportData?.specializations ||
      ['general']
    
    // Determine effective visit type
    const effectiveVisitType = visitType || 
                                reportData?.visitType ||
                                (reportData?.patientMeetingSeqNumber > 1 ? 'repeat' : 'new')
    
    const promise = getSectionsAndQuestionsBasedOnFilledAnswers({
      sections: screeningSections,
      answers: screeningQuestionAnswers,
      fetchSectionsIfMissing: needsFetch, // Only fetch if sections are completely missing
      doctorSpecializations: effectiveSpecializations,
      visitType: effectiveVisitType,
      useDebugNumbering: useDebugNumbering,
      screeningForSpecializations: screeningForSpecializationsArray, // Pass for potential API fetch
    })
    
    promise
      .then((result) => {
        setSectionsWithAnswers(result || [])
        // Only mark as processed AFTER we successfully get results
        // This prevents skipping on next render if the async call hasn't completed yet
        if (result && result.length > 0) {
          lastProcessedKeyRef.current = processKey
        } else {
          // Clear the key so we can retry on next render
          lastProcessedKeyRef.current = ''
        }
      })
      .catch((error) => {
        console.error('[ScreeningReportBodyFirstSection] getSectionsAndQuestionsBasedOnFilledAnswers error:', error)
        setSectionsWithAnswers([])
        // Clear the key so we can retry
        lastProcessedKeyRef.current = ''
      })
  }, [
    // Only depend on lengths to prevent unnecessary re-runs
    screeningSections?.length, 
    screeningQuestionAnswers?.length,
    doctorSpecializations, // Added to dependencies
    visitType, // Added to dependencies
    screeningForSpecializations, // Added to dependencies
    reportData?.doctorSpecializations,
    reportData?.mentorSpecializations,
    reportData?.specializations,
    reportData?.visitType,
    reportData?.patientMeetingSeqNumber,
    useDebugNumbering,
  ])


  const isComplaintsValid = Array.isArray(selectedComplaints) && selectedComplaints?.length > 0
  const isDiagnosisValid = Array.isArray(selectedDiagnosis) && selectedDiagnosis?.length > 0
  const isObservationsValid = observationAndAdvice?.observation
  const isScreeningAnswersValid = !!sectionsWithAnswers?.length
  const isPrimaryClinicalDiagnosisValid = primaryClinicalDiagnosis && (primaryClinicalDiagnosis?.title || primaryClinicalDiagnosis?.label || primaryClinicalDiagnosis?.value)
  const isDifferentialClinicalDiagnosesValid = Array.isArray(differentialClinicalDiagnoses) && differentialClinicalDiagnoses?.length > 0

  const hasAnyContent = isDiagnosisValid || isComplaintsValid || isObservationsValid || isScreeningAnswersValid || isPrimaryClinicalDiagnosisValid || isDifferentialClinicalDiagnosesValid

  return (
    <>
      <div className="w-100 fs-6" style={{ textAlign: 'center', textDecoration: 'underline', marginBottom: '24px' }}>
        Screening Report
      </div>
      {hasAnyContent ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0', paddingTop: '0' }}>
          {/* Screening Questions Section */}
          {isScreeningAnswersValid && (
            <>
              {sectionsWithAnswers?.map((section, idx) => {
                // Calculate question number offset: sum of questions in all previous sections
                const questionNumberOffset = sectionsWithAnswers
                  .slice(0, idx)
                  .reduce((total, prevSection) => total + (prevSection?.questions?.length || 0), 0)
                
                return (
                  <SectionCard key={section?.id || `section-${idx}`} $sectionType="screeningQuestions">
                    <SectionHeader $sectionType="screeningQuestions">
                      {section?.title || 'Screening Questions'}
                    </SectionHeader>
                    <SectionContent>
                      <PdfReportSectionHandler
                        section={section}
                        showHeader={false}
                        questionNumberOffset={questionNumberOffset}
                      />
                    </SectionContent>
                  </SectionCard>
                )
              })}
            </>
          )}
          
          {/* Presenting Complaint Section */}
          {isComplaintsValid && (
            <SectionCard $sectionType="presentingComplaint">
              <SectionHeader $sectionType="presentingComplaint">
                Presenting Complaint
              </SectionHeader>
              <SectionContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedComplaints.map((complaint, index) => {
                    const title = complaint?.title || complaint?.label || complaint?.value || ''
                    const hasDetails = complaint?.note || complaint?.onsetTime || complaint?.durationSince
                    
                    if (!hasDetails) {
                      // Simple display if no extra details
                      return <div key={index} style={{ padding: '8px 0' }}>{title}</div>
                    }
                    
                    return (
                      <SubSectionCard key={complaint?.id || complaint?.value || index} $sectionType="presentingComplaint">
                        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '8px', fontSize: '15px' }}>
                          {title}
                        </div>
                        {complaint?.note && (
                          <div style={{
                            fontSize: '14px',
                            color: '#334155',
                            marginTop: '8px',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.6',
                          }}>
                            <DetailLabel>Note:</DetailLabel> {complaint.note}
                          </div>
                        )}
                        {(complaint?.onsetTime || complaint?.durationSince) && (
                          <DetailRow>
                            {complaint?.onsetTime && (
                              <div>
                                <DetailLabel>Onset:</DetailLabel> {dayjs.utc(complaint.onsetTime).local().format('DD MMM YYYY')}
                              </div>
                            )}
                            {complaint?.durationSince && (
                              <div>
                                <DetailLabel>Duration:</DetailLabel> {complaint.durationSince} {complaint.durationType || 'days'}
                              </div>
                            )}
                          </DetailRow>
                        )}
                      </SubSectionCard>
                    )
                  })}
                </div>
              </SectionContent>
            </SectionCard>
          )}
          
          {/* Observations Section */}
          {isObservationsValid && (
            <SectionCard $sectionType="observations">
              <SectionHeader $sectionType="observations">
                Observations
              </SectionHeader>
              <SectionContent>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>
                  {observationAndAdvice?.observation}
                </div>
              </SectionContent>
            </SectionCard>
          )}
          
          {/* Primary Clinical Diagnosis Section */}
          {isPrimaryClinicalDiagnosisValid && (
            <SectionCard $sectionType="primaryClinicalDiagnosis">
              <SectionHeader $sectionType="primaryClinicalDiagnosis">
                Primary Clinical Diagnosis
              </SectionHeader>
              <SectionContent>
                <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
                  {primaryClinicalDiagnosis?.title || primaryClinicalDiagnosis?.label || primaryClinicalDiagnosis?.value}
                </div>
                {primaryClinicalDiagnosis?.note && (
                  <div style={{ fontSize: '14px', color: '#334155', marginTop: '8px', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                    <DetailLabel>Notes:</DetailLabel> {primaryClinicalDiagnosis.note}
                  </div>
                )}
              </SectionContent>
            </SectionCard>
          )}
          
          {/* Differential Clinical Diagnoses Section */}
          {isDifferentialClinicalDiagnosesValid && (
            <SectionCard $sectionType="differentialClinicalDiagnoses">
              <SectionHeader $sectionType="differentialClinicalDiagnoses">
                Differential Clinical Diagnoses
              </SectionHeader>
              <SectionContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {differentialClinicalDiagnoses.map((diagnosis, index) => (
                    <SubSectionCard key={diagnosis?.id || diagnosis?.value || index} $sectionType="differentialClinicalDiagnoses">
                      <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
                        {diagnosis?.title || diagnosis?.label || diagnosis?.value}
                      </div>
                      {diagnosis?.note && (
                        <div style={{ fontSize: '14px', color: '#334155', marginTop: '8px', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          <DetailLabel>Notes:</DetailLabel> {diagnosis.note}
                        </div>
                      )}
                    </SubSectionCard>
                  ))}
                </div>
              </SectionContent>
            </SectionCard>
          )}
          
          {/* Diagnosis Section */}
          {isDiagnosisValid && (
            <SectionCard $sectionType="diagnosis">
              <SectionHeader $sectionType="diagnosis">
                Diagnosis
              </SectionHeader>
              <SectionContent>
                <div style={{ fontSize: '15px', lineHeight: '1.6' }}>
                  {selectedDiagnosis.map((diagnosisItem) => diagnosisItem.label).join(', ')}
                </div>
              </SectionContent>
            </SectionCard>
          )}
        </div>
      ) : (
        <></>
      )}
    </>
  )
}

export const ScreeningReportBody = ({ observationAndAdvice, content, suggestedBookingTherapy }) => {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0', paddingTop: '0' }}>
        {/* Advice Section */}
        {observationAndAdvice?.advice && (
          <SectionCard $sectionType="advice">
            <SectionHeader $sectionType="advice">
              Advice
            </SectionHeader>
            <SectionContent>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>
                {observationAndAdvice?.advice}
              </div>
            </SectionContent>
          </SectionCard>
        )}
        
        {/* Notes Section */}
        {content && (
          <SectionCard $sectionType="notes">
            <SectionHeader $sectionType="notes">
              Screening Report Notes
            </SectionHeader>
            <SectionContent>
              <PrescriptionWrapperForRichText
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </SectionContent>
          </SectionCard>
        )}
        
        {/* Psychotherapy Recommendation */}
        {suggestedBookingTherapy && (
          <SectionCard $sectionType="primaryClinicalDiagnosis">
            <SectionHeader $sectionType="primaryClinicalDiagnosis">
              Psychotherapy Recommendation
            </SectionHeader>
            <SectionContent>
              <div style={{
                fontWeight: '600',
                fontSize: '16px',
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '8px',
                border: '2px solid rgba(245, 158, 11, 0.3)',
              }}>
                Psychotherapy Recommended (and requested): {suggestedBookingTherapy ? 'Yes' : 'No'}
              </div>
            </SectionContent>
          </SectionCard>
        )}
      </div>
    </>
  )
}

const commonStyles = {
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999, // Ensure portal renders above all other components
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '200px',
    overflowY: 'scroll',
    scrollbarWidth: 'thin',
    '::-webkit-scrollbar': {
      width: '5px',
    },
    '::-webkit-scrollbar-track': {
      background: '#fafafa',
    },
    '::-webkit-scrollbar-thumb': {
      background: '#888',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    borderRadius: 0,
    zIndex: 9999, // Ensure dropdown renders above other components
  }),
  control: (provided, state) => ({
    ...provided,
    borderRadius: 0,
    backgroundColor: state.menuIsOpen ? '#fff8dc' : '#FFF',
    border: state.isFocused ? '1px solid green' : provided.border,
    boxShadow: 'none',
    '&:hover': {
      border: '1px solid green',
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none', // Hide the default arrow separator
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'none', // Hide the default dropdown arrow
  }),
}

export const ScreeningReportTextAreaComponent = ({ inputValue, setInputValue }) => {
  return (
    <PMPSectionTextAreaBox 
      value={inputValue || ''} 
      onChange={(e) => setInputValue(e.target.value)}
      style={{
        width: '100%',
        minHeight: '120px',
        padding: '12px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '14px',
        fontFamily: 'inherit',
        color: '#1e293b',
        background: '#ffffff',
        transition: 'all 0.2s ease',
        resize: 'vertical',
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#6779e3'
        e.target.style.boxShadow = '0 0 0 3px rgba(103, 121, 227, 0.1)'
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#e2e8f0'
        e.target.style.boxShadow = 'none'
      }}
      placeholder="Enter details here..."
    />
  )
}

const loadOptionsComplaints = async (search, loadedOptions, { page, clinicId }) => {
  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/health-complaint/list?search=${search}&clinicId=${clinicId}&page=${page}&pageSize=5`,
  )
  var hasMore = true
  var optionsArray = []

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    optionsArray = response?.data?.map((option, index) => {
      return {
        ...option,
        value: option?.title,
        label: option?.title,
      }
    })
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
    },
  }
}

const loadOptionsDiagnosis = async (search, loadedOptions, { page, clinicId }) => {
  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/diagnosis/list?search=${search}&clinicId=${clinicId}&page=${page}&pageSize=5`,
  )
  var hasMore = true
  var optionsArray = []

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    optionsArray = response?.data?.map((option, index) => {
      return {
        ...option,
        value: option?.title,
        label: option?.title,
      }
    })
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
    },
  }
}

export const DiagnosisSelectMenu = ({ clinicId, selectedDiagnosis, setSelectedDiagnosis }) => {
  const CreatableAsyncPaginate = withAsyncPaginate(Creatable)

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <CreatableAsyncPaginate
        id="diagnosisSelection"
        placeholder=""
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        defaultOptions={false}
        loadOptions={loadOptionsDiagnosis}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={selectedDiagnosis}
        onChange={(value) => {
          setSelectedDiagnosis(value)
        }}
        debounceTimeout={500}
        additional={{ page: 1, clinicId: clinicId }}
        styles={commonStyles}
      />
    </div>
  )
}

///// helper functions:

// complaints array related:

export function getComplaintsArrayForSubmission(complaintsArray, clinicId) {
  const toReturn = []
  if (Array.isArray(complaintsArray) && complaintsArray.length > 0) {
    complaintsArray?.forEach((complaintItem) => {
      if (complaintItem?.label) {
        toReturn.push({
          apiParam: 'health-complaint',
          clinicId: clinicId,
          title: complaintItem?.label,
        })
      }
    })
  }
  return toReturn
}

export const getSRInitialDataForSettingExistingSelectedComplaints = (complaintsFromServer) => {
  const toReturn = []
  if (Array.isArray(complaintsFromServer) && complaintsFromServer.length > 0) {
    complaintsFromServer?.forEach((item) => {
      // Preserve all fields including note, onsetTime, durationSince, durationType
      toReturn.push({
        label: item?.title || item?.label,
        value: item?.title || item?.value || item?.label,
        title: item?.title || item?.label,
        // Preserve all additional fields
        id: item?.id,
        note: item?.note || null,
        onsetTime: item?.onsetTime || null,
        durationSince: item?.durationSince ?? null,
        durationType: item?.durationType || 'days',
        // Preserve any other fields that might be present
        ...(item?.clinicId && { clinicId: item.clinicId }),
        ...(item?.apiParam && { apiParam: item.apiParam }),
      })
    })
  }
  return toReturn
}

// diagnosis array related:

export function getDiagnosisArrayForSubmission(diagnosisArray, clinicId) {
  const toReturn = []
  if (Array.isArray(diagnosisArray) && diagnosisArray.length > 0) {
    diagnosisArray?.forEach((diagnosisItem) => {
      if (diagnosisItem?.label) {
        toReturn.push({
          apiParam: 'diagnosis',
          clinicId: clinicId,
          title: diagnosisItem?.label,
        })
      }
    })
  }
  return toReturn
}

export const getSRInitialDataForSettingExistingSelectedDiagnosis = (diagnosisFromServer) => {
  const toReturn = []
  if (Array.isArray(diagnosisFromServer) && diagnosisFromServer.length > 0) {
    diagnosisFromServer?.forEach((item) => {
      toReturn.push({ label: item?.title, value: item?.title })
    })
  }
  return toReturn
}

// next date related

export const calculateNextVisitDate = (nextVisitInputValue, nextVisitInputType, format, dateFrom) => {
  // Validate inputs
  if (!nextVisitInputValue || !nextVisitInputType) {
    return format ? '' : null
  }

  // Use dateFrom if provided, otherwise use current date - clone to avoid mutation
  const currentDate = dateFrom ? dayjs(dateFrom) : dayjs()

  // Map the input type to the corresponding moment method
  const methodMap = {
    days: 'add',
    months: 'add',
    weeks: 'add',
  }

  // Calculate the next visit date based on input type and value
  if (methodMap[nextVisitInputType]) {
    currentDate[methodMap[nextVisitInputType]](nextVisitInputValue, nextVisitInputType)
  } else {
    console.error('Invalid input type')
    return format ? '' : null
  }

  if (format) {
    return currentDate.format(format)
  }
  // Return the formatted date (you can adjust the format based on your needs)
  return currentDate.format('DD/MM/YYYY')
}

export const getNextVisitDateForSubmission = (
  nextVisitInputValue,
  nextVisitInputType,
  nextVisitCalendarInput,
  bookedEventInfoStartTime, // Add as optional parameter for backward compatibility
) => {
  if (!nextVisitInputValue || !nextVisitInputType) {
    return null
  }

  // Use bookedEventInfoStartTime if provided, otherwise fall back to current time
  // This maintains backward compatibility with existing calls
  const baseDate = bookedEventInfoStartTime 
    ? dayjs(bookedEventInfoStartTime) 
    : dayjs()

  // Clone the date to avoid mutation
  const currentDate = dayjs(baseDate)

  // Map the input type to the corresponding moment method
  const methodMap = {
    days: 'add',
    months: 'add',
    weeks: 'add',
  }

  // Calculate the next visit date based on input type and value
  if (methodMap[nextVisitInputType]) {
    currentDate[methodMap[nextVisitInputType]](nextVisitInputValue, nextVisitInputType)
  } else {
    console.error('Invalid input type')
    return null
  }
  
  // Convert to UTC before formatting - the time value represents UTC time
  return currentDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

export const getInitialDataForSettingDaysOfNextVisit = (nextDate, createdTime) => {
  if (nextDate) {
    // Parse nextDate - it comes from server as UTC
    const nextDateMoment = dayjs.utc(nextDate)
    
    if (createdTime) {
      // Parse createdTime as UTC as well (server stores in UTC)
      const createdTimeMoment = dayjs.utc(createdTime, 'YYYY-MM-DD HH:mm:ss')
      return nextDateMoment.diff(createdTimeMoment, 'days')
    } else {
      // Compare with current time in UTC
      return nextDateMoment.diff(dayjs.utc(), 'days')
    }
  }
  return null
}

export const PrescriptionWrapperForRichText = styled.div`
  position: relative;
  width: 100%;
  ${(props) => props?.premium && 'height: 300px; overflow: hidden;'};
  color: #242424;
  font-size: 13px;
  p {
    font-size: 1rem;
    strong {
      font-weight: 500;
    }
  }
  h1 {
    font-size: 1.375rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    line-height: 1.2;
  }
  h2 {
    font-size: 1.325rem;
    font-weight: 500;
    line-height: 1.2;
    margin-bottom: 0.5rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 500;
    line-height: 1.2;
    margin-bottom: 0.5rem;
  }
  h4 {
    font-size: 1.275rem;
    font-weight: 500;
    line-height: 1.2;
    margin-bottom: 0.5rem;
  }
  h5 {
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.2;
    margin-bottom: 0.5rem;
  }
  h6 {
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.2;
    margin-bottom: 0.5rem;
  }
  img {
    margin: auto;
    border: 1px solid #f4f4f4;
  }
  a {
    font-size: 1rem;
  }
  img {
    width: 100%;
  }
  blockquote {
    font-size: 1rem;
    margin-bottom: 1rem;
    padding-left: 5px;
  }
  ol {
    list-style-type: decimal;
    padding-left: 20px;
    margin: 16px 0;
  }
  ul {
    list-style-type: disc;
    padding-left: 20px;
    margin: 16px 0;
  }
  li {
    font-size: 1rem;
  }
  a {
    color: #2819af;
    text-decoration: underline;
  }
  p,
  strong,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  img,
  a,
  blockquote,
  ol,
  ul,
  li {
    font-size: 13px;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  @media (min-width: 1200px) {
    font-size: 13px;
    p {
      strong {
        font-weight: 500;
      }
    }
    h1 {
      font-size: 2.5rem;
      font-weight: 500;
      margin-bottom: 0.5rem;
      line-height: 1.2;
    }
    h2 {
      font-size: 2rem;
      font-weight: 500;
      line-height: 1.2;
      margin-bottom: 0.5rem;
    }
    h3 {
      font-size: 1.75rem;
      font-weight: 500;
      line-height: 1.2;
      margin-bottom: 0.5rem;
    }
    h4 {
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 1.2;
      margin-bottom: 0.5rem;
    }
    p,
    strong,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    img,
    a,
    blockquote,
    ol,
    ul,
    li {
      font-size: 13px;
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 0;
      padding-bottom: 0;
    }
  }
`
